// 261015 P4c-1 (D8/Q3) — hộp sửa trực tiếp MỘT mục của tổng quan (đặc tả trường + kiểm + dựng lệnh ở `overviewEdit.ts`, thuần, có jest).
// Lưu = một PATCH nhiều lệnh (do `onSubmit` thực hiện, kèm `expectedModifiedAt`). Portal ngoài `.wb` ⇒ chỉ antd + style trung tính.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Space, Typography } from 'antd';
import type { FilmOutline, Overview, OutlinePatchOp } from '../../../../types/adaptation';
import {
  buildOps, optionsFor, quoteWarnings, validateForm, type EditTarget, type FieldSpec, type FormSpec, type Values,
} from './overviewEdit';

const { Text } = Typography;

interface Props {
  spec: FormSpec | null;
  target: EditTarget;
  ov: Overview;
  outline: FilmOutline | null | undefined;
  onCancel: () => void;
  /** Trả true khi đã lưu (đóng hộp); false/ném ⇒ giữ hộp mở để sửa tiếp. */
  onSubmit: (ops: OutlinePatchOp[]) => Promise<boolean>;
}

const QuotesField: React.FC<{ value: string[]; onChange: (v: string[]) => void; disabled: boolean }> = ({ value, onChange, disabled }) => {
  const list = value.length ? value : [''];
  return (
    <div>
      {list.map((q, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
          <Input.TextArea value={q} disabled={disabled} autoSize={{ minRows: 2, maxRows: 8 }} placeholder={`Trích ${i + 1} (nguyên văn, liên tục)`} onChange={(e) => onChange(list.map((x, j) => (j === i ? e.target.value : x)))} />
          <Button size="small" disabled={disabled} onClick={() => onChange(list.filter((_x, j) => j !== i))} aria-label={`Bỏ trích ${i + 1}`}>Bỏ</Button>
        </div>
      ))}
      {list.length < 4 && <Button size="small" disabled={disabled} onClick={() => onChange([...list, ''])}>Thêm trích</Button>}
    </div>
  );
};

const OverviewEditModal: React.FC<Props> = ({ spec, target, ov, outline, onCancel, onSubmit }) => {
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValues(spec ? { ...spec.initial } : {}); setErrors([]); setSaving(false); }, [spec]);

  const set = (key: string, v: unknown): void => {
    setValues((cur) => {
      const next = { ...cur, [key]: v };
      if (key === 'purpose') next.subjectId = '';
      if (key === 'disposition' && (v === 'CUT' || v === 'UNMAPPED')) next.characterId = '';
      return next;
    });
    setErrors([]);
  };

  const warnings = useMemo(() => (spec && (spec.kind === 'claim' || spec.kind === 'claim-add') ? quoteWarnings(values.quotes ?? []) : []), [spec, values.quotes]);

  const submit = async (): Promise<void> => {
    if (!spec) return;
    const errs = validateForm(spec.kind, values, ov, target);
    if (errs.length) { setErrors(errs); return; }
    setSaving(true);
    try { if (await onSubmit(buildOps(spec.kind, values, target, spec.initial))) onCancel(); } finally { setSaving(false); }
  };

  const doRemove = (): void => {
    if (!spec?.remove) return;
    const r = spec.remove;
    Modal.confirm({
      title: `${r.label}?`, content: r.confirm, okText: r.label, cancelText: 'Thôi', okButtonProps: { danger: true },
      onOk: async () => { setSaving(true); try { if (await onSubmit(r.ops)) onCancel(); } finally { setSaving(false); } },
    });
  };

  const renderField = (f: FieldSpec): React.ReactNode => {
    const v = values[f.key];
    const opts = f.options ?? (f.source ? optionsFor(f.source, ov, outline, { purpose: values.purpose }) : []);
    const disabled = saving || (f.key === 'characterId' && (values.disposition === 'CUT' || values.disposition === 'UNMAPPED'));
    switch (f.type) {
      case 'text': return <Input value={v ?? ''} disabled={disabled} onChange={(e) => set(f.key, e.target.value)} />;
      case 'textarea': return <Input.TextArea value={v ?? ''} disabled={disabled} autoSize={{ minRows: f.rows ?? 3, maxRows: 10 }} onChange={(e) => set(f.key, e.target.value)} />;
      case 'select': return <Select style={{ width: '100%' }} value={v === '' || v == null ? undefined : v} disabled={disabled} allowClear={!!f.allowEmpty} showSearch optionFilterProp="label" options={opts} placeholder="Chọn…" onChange={(x) => set(f.key, x ?? '')} />;
      case 'multi': return <Select mode="multiple" style={{ width: '100%' }} value={v ?? []} disabled={disabled} showSearch optionFilterProp="label" options={opts} placeholder="Chọn…" onChange={(x) => set(f.key, x)} />;
      case 'tags': return <Select mode="tags" style={{ width: '100%' }} value={v ?? []} disabled={disabled} open={false} tokenSeparators={[',']} placeholder="Gõ tên rồi Enter" onChange={(x) => set(f.key, x)} />;
      case 'quotes': return <QuotesField value={v ?? []} disabled={disabled} onChange={(x) => set(f.key, x)} />;
      default: return null;
    }
  };

  return (
    <Modal
      open={!!spec} title={spec?.title} onCancel={onCancel} width={640} destroyOnHidden maskClosable={false}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div>{spec?.remove && <Button danger disabled={saving} onClick={doRemove}>{spec.remove.label}</Button>}</div>
          <Space><Button onClick={onCancel} disabled={saving}>Thôi</Button><Button type="primary" loading={saving} onClick={submit}>{spec?.okText ?? 'Lưu'}</Button></Space>
        </div>
      )}
    >
      {spec && (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Lưu sẽ đưa khung xương về bản nháp — cần "Chốt tổng quan" lại. Nhân vật, cao trào, dàn tập và kịch bản giữ nguyên.</Text>
          {spec.fields.map((f) => (
            <div key={f.key} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{f.label}{f.required ? ' *' : ''}</label>
              {renderField(f)}
              {f.help && <Text type="secondary" style={{ fontSize: 13 }}>{f.help}</Text>}
            </div>
          ))}
          {warnings.length > 0 && <Alert type="warning" showIcon message={warnings.map((w, i) => <div key={i}>{w}</div>)} />}
          {errors.length > 0 && <Alert type="error" showIcon message={errors.map((w, i) => <div key={i}>{w}</div>)} />}
        </Space>
      )}
    </Modal>
  );
};

export default OverviewEditModal;
